import BusinessSection from './components/BusinessSection';
import { SEO } from '../../utils/seo';

export default function BusinessPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '事業紹介 - Business',
    description: 'インドネシアでのステーブルコイン事業、AI健康アプリ「クラティブ」、コーヒー輸入、防水コンクリート技術。テクノロジーを活用し、アジアと日本の暮らしを豊かにする4つの事業をご紹介します。',
    url: `${siteUrl}/business`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: [
        {
          '@type': 'Service',
          name: 'インドネシアでのステーブルコイン発行・運用プロジェクト',
          description: '法定通貨と価値を連動させたステーブルコインを活用し、低コストかつ高速な送金、安全なオンライン決済を実現するインフラづくり。',
          provider: {
            '@type': 'Organization',
            name: '株式会社ガイアライブラリー'
          }
        },
        {
          '@type': 'Service',
          name: 'コーヒーを中心としたフード輸入事業',
          description: 'インドネシア・アジア地域から、こだわりのコーヒー豆やスパイスなど食品を輸入し、日本国内のカフェ・小売店・オンラインストアなどへ展開。',
          provider: {
            '@type': 'Organization',
            name: '株式会社ガイアライブラリー'
          }
        },
        {
          '@type': 'SoftwareApplication',
          name: 'クラティブ - 地域密着型総合アプリ',
          applicationCategory: 'HealthApplication',
          description: '医療・商業・公共施設・交通など生活に必要な情報を集約した生活ポータルアプリ。対話型AIによる健康問診、服薬支援、施設検索・予約機能を提供。',
          operatingSystem: 'iOS, Android, Web',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'JPY'
          }
        },
        {
          '@type': 'Service',
          name: '防水コンクリート・新素材によるインフラDX',
          description: '防水性能を高める新素材や、補修効率を高める工法など、インフラの長寿命化に寄与する技術の普及。',
          provider: {
            '@type': 'Organization',
            name: '株式会社ガイアライブラリー'
          }
        }
      ]
    }
  };

  const businesses = [
    {
      id: 1,
      title: 'インドネシアでのステーブルコイン発行・運用プロジェクト',
      content: `インドネシアは、急速にデジタル決済が普及する一方で、
銀行口座を持たない人々や地方部の決済インフラには、まだ多くの課題が残されています。

私たちは、法定通貨と価値を連動させたステーブルコインを活用し、`,
      features: [
        '低コストかつ高速な送金',
        '安全なオンライン決済',
        '小規模事業者・地方コミュニティでのデジタル支払い'
      ],
      footer: `を実現するインフラづくりを進めています。

ブロックチェーン技術によりトランザクションの透明性・改ざん耐性を確保し、
現地パートナーと連携しながら、規制環境に配慮した形で事業を展開していきます。`,
      image: 'https://public.readdy.ai/ai/img_res/00079b87c132d99e9d7b5916df208a9f.jpg',
      reverse: false
    },
    {
      id: 2,
      title: 'コーヒーを中心としたフード輸入事業',
      content: `インドネシア・アジア地域から、こだわりのコーヒー豆やスパイスなど食品を輸入し、
日本国内のカフェ・小売店・オンラインストアなどへ展開しています。

現地生産者との直接的なつながりを重視し、`,
      features: [
        '生産背景がわかるトレーサビリティ',
        'フェアな価格設定',
        '継続的な品質向上'
      ],
      footer: `を共に目指す、パートナーシップ型のビジネスモデルです。

将来的には、ステーブルコインやデジタル決済と組み合わせることで、
生産者への支払いと物流・在庫管理までをシームレスにつなぐ仕組みの構築も視野に入れています。`,
      image: 'https://readdy.ai/api/search-image?query=Premium%20specialty%20coffee%20beans%20from%20Indonesia%2C%20coffee%20plantation%20with%20farmers%20harvesting%20beans%2C%20direct%20trade%20coffee%20import%20business%20with%20simple%20clean%20background%2C%20high%20quality%20arabica%20coffee%20in%20professional%20setting%20with%20blue%20accents&width=800&height=600&seq=business-002&orientation=landscape',
      reverse: true
    },
    {
      id: 3,
      title: '地域密着型 総合アプリ「クラティブ」',
      content: `「クラティブ」は、地域住民向けに、
医療・商業・公共施設・交通など生活に必要な情報を集約した生活ポータルアプリです。
スマートフォンやタブレット、PC、テレビなど複数デバイスで利用でき、
若者から高齢者まで、誰にとっても使いやすい設計を目指しています。`,
      features: [
        '対話型AIによる日常会話・簡易健康問診・服薬支援',
        '健康情報システム（診療歴・薬歴・介護履歴の住民側一元管理）',
        'カレンダー連携（通院・服薬・各種サービス予約を自動で管理）',
        '家事代行・給食・生活支援サービスの申し込み',
        '動画・音楽・書籍配信などのエンターテイメント機能',
        '音声操作・なんでも相談窓口・施設検索／予約機能'
      ],
      footer: `高齢者や障がいのある方にも配慮したバリアフリーなUIと、
AIによる対話・音声操作により、「複雑なボタン操作を覚えなくても使える」ことを重視しています。

また、医療・介護に関わるデータは、改ざん耐性の高いブロックチェーンをベースとした仕組みで
安全に管理し、「安心して使える健康アプリ」として自治体・医療機関との連携も見据えています。`,
      image: 'https://readdy.ai/api/search-image?query=AI%20health%20and%20lifestyle%20mobile%20application%20interface%2C%20elderly%20person%20using%20smartphone%20with%20healthcare%20app%2C%20digital%20health%20management%20system%20with%20clean%20modern%20design%2C%20accessible%20technology%20for%20all%20ages%20with%20blue%20interface%20elements&width=800&height=600&seq=business-003&orientation=landscape',
      reverse: false
    },
    {
      id: 4,
      title: '防水コンクリート・新素材によるインフラDX',
      content: `建築・土木分野では、「つくる」こと以上に、「長く安全に使い続けること」が重要になっています。

私たちは、防水性能を高める新素材や、補修効率を高める工法など、
インフラの長寿命化に寄与する技術をパートナー企業と共に普及させていきます。`,
      features: [
        'コンクリート構造物のひび割れ・浸水対策',
        '建物の雨漏り防止・防水性向上',
        'メンテナンス回数の削減によるトータルコストの低減'
      ],
      footer: `こうした技術は、災害に強いまちづくりや、老朽化したインフラの再生にもつながっていきます。`,
      image: 'https://readdy.ai/api/search-image?query=Waterproof%20concrete%20construction%20technology%2C%20modern%20infrastructure%20building%20materials%20with%20protective%20coating%2C%20durable%20construction%20innovation%20for%20bridges%20and%20buildings%2C%20professional%20engineering%20technology%20with%20clean%20background%20and%20blue%20tones&width=800&height=600&seq=business-004&orientation=landscape',
      reverse: true
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="事業紹介｜福岡ステーブルコインAI健康アプリコーヒー輸入"
        description="インドネシアでのステーブルコイン事業、AI健康アプリ「クラティブ」、コーヒー輸入、防水コンクリート技術。テクノロジーを活用し、アジアと日本の暮らしを豊かにする4つの事業をご紹介します。"
        keywords="ステーブルコイン,クラティブ,AI健康アプリ,コーヒー輸入,防水コンクリート,インドネシア,事業紹介"
        schema={schema}
      />
      {/* Page Header */}
      <div className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-20 px-6 md:px-20">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-widest uppercase mb-4 text-blue-200">Business</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">事業紹介</h1>
          <p className="text-lg text-blue-100 leading-relaxed">
            テクノロジーを活用し、アジアと日本の暮らしを豊かにする4つの事業
          </p>
        </div>
      </div>

      {/* Business Sections */}
      {businesses.map((business) => (
        <BusinessSection key={business.id} {...business} />
      ))}
    </div>
  );
}
